import React, {
  MouseEventHandler,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../ui/dropdown-menu';

import Icon, { IconTypes } from '../icon';
import SearchBar, { Props as SearchBarProps } from '../search-bar';
import { Checkbox, CheckboxOwnProps } from '../form/inputs/checkbox';
import { cn } from '@common/lib/utils';

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick' | 'children'> {
  children: Array<React.ReactNode> | React.ReactNode;
  toggle?: React.ReactNode;
  alignRight?: boolean;
  className?: string;
  small?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onClick?: () => boolean;
  onOpen?: (event?: any, data?: any) => void;
  onClose?: (event?: any, data?: any) => void;
  portal?: boolean;
  keepOpenOnModal?: boolean;
  zIndex?: number;
  disalbed?: boolean;
  triggerClassName?: string;
  contentSide?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Make the menu at least as wide as its trigger. Useful for "select"-style
   * dropdowns (a pill that opens its own options) where a menu narrower than
   * the trigger looks detached. Off by default so compact triggers (e.g. a
   * three-dot row action) keep their content-sized menu.
   */
  matchTriggerWidth?: boolean;
  disabled?: boolean;
  search?: boolean;
  scrolling?: boolean;
  pointing?: boolean | 'left' | 'right' | 'top' | 'bottom' | 'top left' | 'top right' | 'bottom left' | 'bottom right';
  onToggle?: (open: boolean) => void;
  alignLeft?: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top left' | 'top right' | 'bottom left' | 'bottom right';
}

function resolveDropdownPosition(position?: Props['position']): {
  side?: 'top' | 'bottom' | 'left' | 'right';
  alignEnd?: boolean;
} {
  if (!position) return {};
  const [side, alignment] = position.split(' ');
  const resolvedSide = side === 'top' || side === 'bottom' || side === 'left' || side === 'right'
    ? side
    : undefined;
  return {
    side: resolvedSide,
    alignEnd: alignment === 'right',
  };
}

interface DropdownContextValue {
  keepOpenOnModal: boolean;
}

const DropdownContext = createContext<DropdownContextValue>({
  keepOpenOnModal: false,
});

const DropdownComponent = ({
  children,
  toggle,
  alignRight,
  className,
  small,
  defaultOpen,
  open: controlledOpen,
  onOpen,
  onClose,
  onClick,
  portal = false,
  keepOpenOnModal = false,
  zIndex,
  disabled,
  disalbed, // typo prop for backward compatibility
  triggerClassName,
  contentSide,
  matchTriggerWidth,
  search,
  scrolling,
  pointing,
  onToggle,
  alignLeft,
  position,
  ...props
}: Props) => {
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
  const triggerWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (disalbed !== undefined) {
      console.warn('Dropdown: "disalbed" prop is a typo. Please use "disabled" instead.');
    }
  }, [disalbed]);

  const isDisabled = disabled || disalbed;
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const defaultZIndex = zIndex ?? 50;

  if (!children) return null;

  const items = Array.isArray(children) ? children.filter(Boolean) : [children];
  if (items.length === 0) return null;

  const containerClassName = cn('tw-relative tw-inline-block', className, {
    'tw-opacity-50 tw-pointer-events-none': isDisabled,
  });

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }

    onToggle?.(nextOpen);
    if (nextOpen) {
      onOpen?.();
    } else {
      onClose?.();
    }
  }, [isControlled, onOpen, onClose, onToggle]);

  const shouldPreventOpen = useCallback(() => {
    if (isDisabled) return true;
    if (!onClick) return false;
    const shouldContinue = onClick();
    return shouldContinue === false;
  }, [isDisabled, onClick]);

  const handleTriggerPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (shouldPreventOpen()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [shouldPreventOpen]);

  const handleTriggerClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (shouldPreventOpen()) {
      event.preventDefault();
    }
  }, [shouldPreventOpen]);

  const contentClassName = cn(
    small && 'tw-text-sm [&_[role=menuitem]]:tw-px-2 [&_[role=menuitem]]:tw-py-1',
  );

  const contentStyle: React.CSSProperties = {
    zIndex: defaultZIndex,
    // Radix exposes the trigger's width as a CSS var on the content; pin the
    // menu to it (both width and min-width, to beat the default min-w-[8rem])
    // so it lines up flush with its trigger on both edges instead of growing
    // to fit content and spilling to one side.
    ...(matchTriggerWidth
      ? {
          width: 'var(--radix-dropdown-menu-trigger-width)',
          minWidth: 'var(--radix-dropdown-menu-trigger-width)',
        }
      : {}),
  };

  const triggerWrapperClassName = cn('tw-inline-flex', triggerClassName || '');

  const triggerNode = useMemo(() => (
    <div
      ref={triggerWrapperRef}
      className={triggerWrapperClassName}
      onPointerDown={handleTriggerPointerDown}
      onClick={handleTriggerClick}
    >
      {toggle}
    </div>
  ), [handleTriggerClick, handleTriggerPointerDown, toggle, triggerWrapperClassName]);

  const contextValue = useMemo(() => ({ keepOpenOnModal }), [keepOpenOnModal]);
  const resolvedPosition = resolveDropdownPosition(position);
  const contentAlign = alignLeft ? 'start' : (alignRight || resolvedPosition.alignEnd) ? 'end' : 'start';

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <DropdownContext.Provider value={contextValue}>
      <div
        className={containerClassName}
        data-dropdown-container
        onClick={handleContainerClick}
        {...props}
      >
        <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} modal={false}>
          <DropdownMenuTrigger asChild disabled={isDisabled}>
            {triggerNode}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={contentAlign}
            disablePortal={!portal}
            className={contentClassName}
            style={contentStyle}
            collisionPadding={8}
            sideOffset={4}
            side={contentSide || resolvedPosition.side || 'bottom'}
          >
            {items}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </DropdownContext.Provider>
  );
};

interface ItemProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick' | 'onSelect'> {
  children: any;
  disabled?: boolean;
  icon?: IconTypes;
  active?: boolean;
  danger?: boolean;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onSelect?: (event: Event) => void;
  stopCloseOnClick?: boolean; // New prop to prevent dropdown from closing
  href?: string;
  as?: any;
  keepOpenOnModal?: boolean;
  selected?: boolean;
  url?: string;
  iconRight?: IconTypes;
}

DropdownComponent.Item = ({
  children,
  danger,
  className,
  icon,
  active,
  keepOpenOnModal,
  disabled,
  href,
  as: AsComponent,
  onClick,
  stopCloseOnClick,
  iconRight,
  selected,
  url,
  ...props
}: ItemProps) => {
  const { onSelect, ...restProps } = props;
  const linkHref = href || url;

  const handleSelect = useCallback((event: Event) => {
    if (stopCloseOnClick) {
      event.preventDefault();
    }

    // Prefer onSelect if provided, otherwise use onClick
    // Event should already have stopPropagation and preventDefault methods
    if (onSelect) {
      onSelect(event);
    } else if (onClick && !linkHref) {
      // Anchor items handle onClick on the real mouse event so cmd/ctrl-click
      // still has modifier keys and can open in a new tab.
      onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
    }
  }, [linkHref, onClick, onSelect, stopCloseOnClick]);

  const classNames = cn(
    'tw-cursor-pointer',
    (active || selected) && 'tw-bg-accent',
    className,
  );

  const renderContent = () => (
    <>
      {icon && <Icon type={icon} />}
      {children}
      {iconRight && <Icon type={iconRight} />}
    </>
  );

  const handleItemClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleModifiedPointerDown = useCallback((event: React.PointerEvent) => {
    // Radix preventDefault on pointerdown blocks browser new-tab / new-window
    // gestures (cmd/ctrl/shift-click, middle-click) on links. Stop the event before Radix.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) {
      event.stopPropagation();
    }
  }, []);

  const handleAnchorClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
  }, [onClick]);

  if (linkHref && !AsComponent) {
    const rest = restProps as {
      className?: string;
      onPointerDown?: React.PointerEventHandler<HTMLAnchorElement>;
      onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    };

    return (
      <DropdownMenuItem
        asChild
        disabled={disabled}
        variant={danger ? 'destructive' : 'default'}
        className={cn(classNames, rest.className)}
        onSelect={handleSelect}
      >
        <a
          href={linkHref}
          {...restProps}
          className={cn(classNames, rest.className)}
          onPointerDown={(event) => {
            handleModifiedPointerDown(event);
            rest.onPointerDown?.(event);
          }}
          onClick={(event) => {
            handleAnchorClick(event);
            rest.onClick?.(event);
          }}
        >
          {renderContent()}
        </a>
      </DropdownMenuItem>
    );
  }

  if (AsComponent) {
    return (
      <DropdownMenuItem
        asChild
        disabled={disabled}
        variant={danger ? 'destructive' : 'default'}
        className={classNames}
        onSelect={handleSelect}
        onClick={handleItemClick}
      >
        <AsComponent {...restProps} {...(url ? { url } : {})} className={classNames}>
          {renderContent()}
        </AsComponent>
      </DropdownMenuItem>
    );
  }

  const sharedProps: React.ComponentProps<typeof DropdownMenuItem> = {
    ...restProps,
    disabled,
    variant: danger ? 'destructive' : 'default',
    className: classNames,
    onSelect: handleSelect,
    onClick: handleItemClick,
  } as React.ComponentProps<typeof DropdownMenuItem>;

  return (
    <DropdownMenuItem
      {...sharedProps}
    >
      {renderContent()}
    </DropdownMenuItem>
  );
};

interface SubMenuProps {
  children: React.ReactNode;
  label: React.ReactNode;
  icon?: IconTypes;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
}

// Nested menu that opens on hover/focus of its trigger row.
DropdownComponent.SubMenu = ({
  children,
  label,
  icon,
  disabled,
  className,
  contentClassName,
}: SubMenuProps) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger
      disabled={disabled}
      className={cn('tw-cursor-pointer', className)}
    >
      {icon && <Icon type={icon} />}
      {label}
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className={contentClassName}>
      {children}
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

DropdownComponent.CheckboxItem = (props: CheckboxOwnProps) => (
  <div
    className="dropdown-checkbox tw-px-2 tw-py-1.5 tw-text-base"
    onClick={(e) => {
      e.stopPropagation();
    }}
  >
    <Checkbox {...props} />
  </div>
);

DropdownComponent.SearchInput = (props: Partial<SearchBarProps> & {
  onSearch: SearchBarProps['onSearch'];
  value?: string;
  placeholder?: string;
}) => (
  <div
    className="tw-px-2 tw-py-1.5 [&_.Form.SearchBar]:tw-m-0"
    onClick={(e) => {
      e.stopPropagation();
    }}
  >
    <SearchBar {...props as any} />
  </div>
);

// Use shadcn's DropdownMenuLabel for Header
DropdownComponent.Header = ({ children, className, ...props }: any) => (
  <DropdownMenuLabel
    className={className}
    {...props}
  >
    {children}
  </DropdownMenuLabel>
);

// Use shadcn's DropdownMenuSeparator for Divider
DropdownComponent.Divider = ({ className, ...props }: any) => (
  <DropdownMenuSeparator
    className={className}
    {...props}
  />
);

// Nested sub-menu: <Dropdown.Sub><Dropdown.SubTrigger/><Dropdown.SubContent>…</Dropdown.SubContent></Dropdown.Sub>
DropdownComponent.Sub = DropdownMenuSub;

type SubTriggerProps = React.ComponentPropsWithoutRef<typeof DropdownMenuSubTrigger> & {
  icon?: IconTypes;
  hideChevron?: boolean;
  className?: string;
  children?: React.ReactNode;
};

function SubTrigger({
  children, icon, className, hideChevron, ...props
}: SubTriggerProps) {
  return (
    <DropdownMenuSubTrigger
      // The chevron is the trailing svg; hide it when hideChevron is set.
      className={cn('tw-cursor-pointer', hideChevron && '[&>svg:last-child]:tw-hidden', className)}
      {...props}
    >
      {icon && <Icon type={icon} />}
      {children}
    </DropdownMenuSubTrigger>
  );
}

DropdownComponent.SubTrigger = SubTrigger;

DropdownComponent.SubContent = DropdownMenuSubContent;

export default DropdownComponent;
